import React, { useCallback, useMemo, useRef } from 'react'
import { Box } from '@mui/material'
import WorldMap, { DataItem } from 'react-svg-worldmap'
import { useContainerDimensions } from 'hooks/useContainerDimensions'
import { YearSlider } from 'components/common/RangeSlider/YearSlider'
// import { formatNumberDecimal } from 'utils/formatNumber'
import { useActions } from 'hooks'
import { formatNumberChart, formatNumberChartStartOfRange, formatNumberDecimal } from 'utils/formatNumber'
export default function WorldmapChart({
  data,
  currentYear,
  showPercentage,
}: {
  data: DataItem<string | number>[]
  currentYear: number
  showPercentage: boolean
}) {
  const { setWorldmapYear } = useActions()
  const values = data.map((obj) => Number(obj.value)).filter((value) => !isNaN(value) && value !== null)
  const minDataValue = values.length ? Math.min(...values) : 0
  const maxDataValue = values.length ? Math.max(...values) : 0

  const containerRef = useRef(null)
  //TODO: re-render too much times
  const { width: widthMap } = useContainerDimensions(containerRef)
  enum RANGECOLOR {
    NODATA = 'rgb(231, 231, 231)',
    ZERO = '#ddebf6',
    RANGE1 = 'rgb(116, 206, 237)',
    RANGE2 = 'rgb(58, 141, 199)',
    RANGE3 = 'rgb(212, 164, 203)',
    RANGE4 = 'rgb(157, 93, 163)',
    RANGE5 = 'rgb(77, 30, 82)',
  }
  interface ColorRange {
    color: string
    rangeBreakpoints: number[]
  }
  const getDynamicCountryColor = (value: number, minValue: number, maxValue: number): ColorRange => {
    const rangeSize = Math.floor((maxValue - minValue) / 5)

    const range1End = minValue + rangeSize
    const range2End = range1End + rangeSize
    const range3End = range2End + rangeSize
    const range4End = range3End + rangeSize
    const range5End = maxValue

    const breakpoints = [minValue, range1End, range2End, range3End, range4End, range5End]
    if (Number.isNaN(value)) {
      return {
        color: RANGECOLOR.NODATA,
        rangeBreakpoints: breakpoints,
      }
    }
    return {
      color:
        value === 0 && range1End > 1
          ? RANGECOLOR.ZERO
          : value >= minValue
          ? value <= range1End
            ? RANGECOLOR.RANGE1
            : value <= range2End
            ? RANGECOLOR.RANGE2
            : value <= range3End
            ? RANGECOLOR.RANGE3
            : value <= range4End
            ? RANGECOLOR.RANGE4
            : RANGECOLOR.RANGE5
          : RANGECOLOR.NODATA,
      rangeBreakpoints: breakpoints,
    }
  }
  const stylingFunction = (context: any) => {
    const value = Number(context.countryValue)
    // const color = getCountryColor(value)
    const colorRange = getDynamicCountryColor(value, minDataValue, maxDataValue)
    const color = colorRange.color
    context.maxValue = maxDataValue
    return {
      fill: color,
      stroke: 'blue',
      strokeWidth: 1,
      strokeOpacity: 0.2,
      cursor: 'default',
    }
  }
  const tooltipTextFunction = (context: any) => {
    return showPercentage
      ? `${context.countryName} \n ${formatNumberDecimal(Number(context.countryValue), 2)}%`
      : `${context.countryName} \n ${formatNumberChart(Number(context.countryValue), 2).toString()}`
  }

  const getRangeColor = getDynamicCountryColor(0, minDataValue, maxDataValue)
  const colorRange = getRangeColor?.rangeBreakpoints

  const decimals = useMemo(() => {
    return maxDataValue < 1000 ? 2 : 0
  }, [maxDataValue])

  const startOfRange = useCallback(
    (num: number) => {
      let tempNum = num
      if (Number(num) >= 1000000000000) {
        return formatNumberChartStartOfRange(num, 0, false, true).toString()
      } else if (Number(num) >= 1000000000) {
        return formatNumberChartStartOfRange(num, 0, false, true).toString()
      } else if (Number(num) >= 1000000) {
        return formatNumberChartStartOfRange(num, 0, false, true).toString()
      } else if (Number(num) >= 1000 && Number(num) < 1000000) {
        return formatNumberChartStartOfRange(num, 0, false, true).toString()
      }
      return formatNumberChart(tempNum, decimals).toString()
    },
    [decimals],
  )

  const hasData = useMemo(() => {
    return !!values.length
  }, [values])
  return (
    <Box
      ref={containerRef}
      sx={{ height: 'calc(100% - 120px)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
    >
      {hasData ? (
        <WorldMap
          size={widthMap}
          data={data}
          color={RANGECOLOR.NODATA}
          richInteraction={true}
          tooltipBgColor={'rgba(199, 220, 239, 1)'}
          tooltipTextColor={'#000'}
          styleFunction={stylingFunction}
          tooltipTextFunction={tooltipTextFunction}
        />
      ) : (
        <Box flex={1}>
          <span>No data available</span>
        </Box>
      )}
      <Box>
        {hasData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '30px', flexWrap: 'wrap' }}>
            <ColorCard color={RANGECOLOR.NODATA} text='No data' />
            {minDataValue === 0 && colorRange[1] > 1 ? <ColorCard color={RANGECOLOR.ZERO} text='0' /> : null}
            <ColorCard
              color={RANGECOLOR.RANGE1}
              // minValue={formatNumberChart(colorRange[0], decimals).toString()}
              minValue={formatNumberChart(
                colorRange[0] === 0 && colorRange[1] > 1 ? 1 : colorRange[0],
                decimals,
              ).toString()}
              maxValue={formatNumberChart(colorRange[1], decimals).toString()}
              showPercentage={showPercentage}
            />
            <ColorCard
              color={RANGECOLOR.RANGE2}
              // minValue={formatNumberChart(colorRange[1], decimals).toString()}
              minValue={startOfRange(colorRange[1])}
              maxValue={formatNumberChart(colorRange[2], decimals).toString()}
              showPercentage={showPercentage}
            />
            <ColorCard
              color={RANGECOLOR.RANGE3}
              // minValue={formatNumberChart(colorRange[2], decimals).toString()}
              minValue={startOfRange(colorRange[2])}
              maxValue={formatNumberChart(colorRange[3], decimals).toString()}
              showPercentage={showPercentage}
            />
            <ColorCard
              color={RANGECOLOR.RANGE4}
              // minValue={formatNumberChart(colorRange[3], decimals).toString()}
              minValue={startOfRange(colorRange[3])}
              maxValue={formatNumberChart(colorRange[4], decimals).toString()}
              showPercentage={showPercentage}
            />
            <ColorCard
              color={RANGECOLOR.RANGE5}
              // minValue={formatNumberChart(colorRange[4], decimals).toString()}
              minValue={startOfRange(colorRange[4])}
              maxValue={formatNumberChart(colorRange[5], decimals).toString()}
              showPercentage={showPercentage}
            />
          </Box>
        ) : null}
        <Box sx={{ mt: '30px', px: '30px' }}>
          <YearSlider value={currentYear} onChange={setWorldmapYear} />
        </Box>
      </Box>
    </Box>
  )
}

export const ColorCard: React.FC<{
  color: string
  text?: string
  minValue?: string
  maxValue?: string
  showPercentage?: boolean
}> = ({ color, text, minValue, maxValue, showPercentage }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
      <Box sx={{ background: color, width: '60px', height: '10px', borderRadius: '10px' }}></Box>
      {text || minValue === maxValue ? (
        <Box>
          <span style={{ fontSize: 14 }}>
            {text ?? minValue}
            {showPercentage ? '%' : ''}
          </span>
        </Box>
      ) : (
        <span style={{ fontSize: 14 }}>
          {minValue}
          {showPercentage ? '%' : ''} - {maxValue}
          {showPercentage ? '%' : ''}
        </span>
      )}
    </Box>
  )
}
